/**
 * Helper functions for room-related operations
 */

/**
 * Format member count display
 * @param {Number} count - Current member count
 * @param {Number} max - Maximum capacity (default 8)
 * @returns {String} Formatted string like "4/8 members"
 */
export const formatMemberCount = (count, max = 8) => {
  return `${count}/${max} member${count !== 1 ? "s" : ""}`;
};

/**
 * Determine user's role in a room
 * @param {Object} room - The room object
 * @param {String} userId - Current user's ID
 * @returns {Object} { isLeader, isMember, role }
 */
export const getUserRoleInRoom = (room, userId) => {
  if (!room || !userId) {
    return { isLeader: false, isMember: false, role: null };
  }

  // Check if user is the leader
  const leaderId = room.leader?._id || room.leader?.id || room.leader;
  if (leaderId === userId) {
    return { isLeader: true, isMember: true, role: "leader" };
  }

  // Check if user is a member
  const member = room.members?.find(
    (m) => (m.user?._id || m.user?.id || m.user) === userId
  );

  if (member) {
    return { isLeader: false, isMember: true, role: member.role || "member" };
  }

  return { isLeader: false, isMember: false, role: null };
};

/**
 * Check if user can manage the room
 * @param {Object} room - The room object
 * @param {String} userId - Current user's ID
 * @returns {Boolean}
 */
export const canManageRoom = (room, userId) => {
  const { isLeader } = getUserRoleInRoom(room, userId);
  return isLeader;
};

/**
 * Get room status for a user
 * @param {Object} room - The room object
 * @param {String} userId - Current user's ID
 * @returns {String} Status: "leader", "member", "requested", "full", "available"
 */
export const getRoomStatus = (room, userId) => {
  if (!room || !userId) return "available";

  // PRIORITY 1: Use API's pre-computed userRelationship (from discovery endpoints)
  // This is the authoritative source since backend calculates it fresh
  if (room.userRelationship) {
    if (room.userRelationship.isLeader) return "leader";
    if (room.userRelationship.isMember) return "member";
    if (room.userRelationship.hasRequestedToJoin) return "requested";
    
    // If userRelationship exists but none of the above, check if room is full
    const memberCount = room.memberCount || room.members?.length || 0;
    const maxMembers = room.maxMembers || 8;
    if (memberCount >= maxMembers) return "full";
    
    return "available";
  }

  // FALLBACK: Calculate from member/request arrays (for detailed room views)
  const { isLeader, isMember } = getUserRoleInRoom(room, userId);

  if (isLeader) return "leader";
  if (isMember) return "member";

  // Check if user has pending join request from joinRequests array
  const hasRequest = room.joinRequests?.some(
    (req) => (req.user?._id || req.user?.id || req.user) === userId
  );
  if (hasRequest) return "requested";

  // Check if room is full
  const memberCount = room.memberCount || room.members?.length || 0;
  const maxMembers = room.maxMembers || 8;
  if (memberCount >= maxMembers) return "full";

  return "available";
};

/**
 * Get button text and style based on room status
 * @param {String} status - Room status from getRoomStatus
 * @returns {Object} { text, className, disabled }
 */
export const getRoomActionButton = (status) => {
  const buttons = {
    leader: {
      text: "Manage Lab",
      className: "btn-manage",
      disabled: false,
    },
    member: {
      text: "Enter Lab",
      className: "btn-enter",
      disabled: false,
    },
    requested: {
      text: "Request Sent",
      className: "btn-requested",
      disabled: true,
    },
    full: {
      text: "Lab Full",
      className: "btn-full",
      disabled: true,
    },
    available: {
      text: "Request to Join",
      className: "btn-join",
      disabled: false,
    },
  };

  return buttons[status] || buttons.available;
};

/**
 * Format date to relative time
 * @param {String|Date} date - Date to format
 * @returns {String} Relative time like "2 hours ago"
 */
export const formatTimeAgo = (date) => {
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }
  const months = Math.floor(diffInSeconds / 2592000);
  return `${months} month${months > 1 ? "s" : ""} ago`;
};

/**
 * Format last activity time for rooms
 * Uses lastActivityAt which tracks messages, tasks, resources, events, and member joins
 * @param {String|Date} date - Date to format
 * @returns {String} Formatted activity time like "Active 5m ago"
 */
export const formatLastActive = (date) => {
  if (!date) return "No activity";

  const now = new Date();
  const lastActive = new Date(date);
  const diffMs = now - lastActive;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMins < 1) return "Active just now";
  if (diffMins < 60) return `Active ${diffMins}m ago`;
  if (diffHours < 24) return `Active ${diffHours}h ago`;
  if (diffDays === 1) return "Active yesterday";
  if (diffDays < 7) return `Active ${diffDays} days ago`;
  if (diffDays < 30) return `Active ${diffWeeks} ${diffWeeks === 1 ? "week" : "weeks"} ago`;
  return `Active ${diffMonths} ${diffMonths === 1 ? "month" : "months"} ago`;
};

/**
 * Truncate text to specified length
 * @param {String} text - Text to truncate
 * @param {Number} maxLength - Maximum length
 * @returns {String} Truncated text with ellipsis
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

/**
 * Get field image path
 * @param {String} field - Field name
 * @returns {String} Image path
 */
export const getFieldImage = (field) => {
  if (!field) return "/field-images/default.jpg";

  const fieldName = field.toLowerCase().replace(/\s+/g, "-");
  return `/field-images/${fieldName}.jpg`;
};

/**
 * Validate room form data
 * @param {Object} formData - Form data to validate
 * @returns {Object} { isValid, errors }
 */
export const validateRoomForm = (formData) => {
  const errors = {};

  if (!formData.name || formData.name.trim().length < 15) {
    errors.name = "Room name must be at least 15 characters";
  }

  if (!formData.description || formData.description.trim().length < 30) {
    errors.description = "Description must be at least 30 characters";
  }

  if (!formData.field) {
    errors.field = "Please select a field";
  }

  if (!formData.subField) {
    errors.subField = "Please select a subfield";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};
